import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, Plus, LogIn, Pencil, QrCode, X, Download } from 'lucide-react'
import Layout from '../components/Layout'
import api from '../api/client'
import { useTenantStore } from '../store/tenant'
import { compressImage } from '../lib/image'

// Super-admin back-office: provision + edit branches (สาขา). Each branch is a
// separate Postgres schema with its own users. "เข้าจัดการ" switches the SPA into
// that branch. Edit covers name / subdomain / card image; QR opens direct entry.
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

// The public URL someone lands on for a branch.
function branchUrl(b) {
  const host = window.location.hostname
  const sub = b.subdomain || b.slug
  const isRealApex = !/^\d+\.\d+\.\d+\.\d+$/.test(host) && host !== 'localhost' && host.split('.').length === 2
  if (isRealApex) return `${window.location.protocol}//${sub}.${host}`
  return `${window.location.origin}/login?branch=${b.slug}`
}

function EditModal({ branch, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: branch.name || '', subdomain: branch.subdomain || branch.slug || '',
    card_image: branch.card_image || '', force_camera: !!branch.force_camera,
    require_department_sign: !!branch.require_department_sign,
  })
  const [saving, setSaving] = useState(false)
  const [qr, setQr] = useState(null)
  const [err, setErr] = useState('')

  const pickImage = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setErr('')
    // ย่อรูปการ์ดก่อนเก็บ base64 ใน DB (เดิมเก็บไฟล์ดิบ ≤1.5MB → row อ้วน)
    const small = await compressImage(file, { maxDim: 800, quality: 0.7 })
    const reader = new FileReader()
    reader.onload = () => setForm((f) => ({ ...f, card_image: reader.result }))
    reader.readAsDataURL(small)
  }

  const save = async () => {
    setSaving(true); setErr('')
    try {
      await api.patch(`/branches/${branch.id}`, {
        name: form.name.trim(),
        subdomain: form.subdomain ? slugify(form.subdomain) : undefined,
        card_image: form.card_image,
        force_camera: form.force_camera,
        require_department_sign: form.require_department_sign,
      })
      onSaved()
    } catch (e) { setErr(e.response?.data?.error || 'บันทึกไม่สำเร็จ') } finally { setSaving(false) }
  }

  const makeQr = async () => {
    setErr('')
    try {
      const { data } = await api.get(`/branches/${branch.id}/qr`, { params: { url: branchUrl(branch) } })
      setQr(data.qr)
    } catch (e) { setErr(e.response?.data?.error || 'สร้าง QR ไม่สำเร็จ') }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-ink text-lg">แก้ไขสาขา</h3>
          <button onClick={onClose} className="text-ink-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex flex-col gap-3">
          <div><label className="label">ชื่อสาขา</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <label className="label">Subdomain</label>
            <input className="input" value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value })} />
            <p className="text-xs text-ink-muted mt-1">{(form.subdomain ? slugify(form.subdomain) : branch.slug)}.&lt;domain&gt; · schema {branch.schema_name} (แก้ schema ไม่ได้)</p>
          </div>
          <div>
            <label className="label">รูปหน้า Card</label>
            <div className="flex items-center gap-3">
              {form.card_image
                ? <img src={form.card_image} alt="" className="w-14 h-14 rounded-xl object-cover border border-line" />
                : <div className="w-14 h-14 rounded-xl bg-primary-soft flex items-center justify-center"><Building2 className="h-6 w-6 text-primary" /></div>}
              <input type="file" accept="image/*" onChange={pickImage} className="text-sm" />
              {form.card_image && <button onClick={() => setForm({ ...form, card_image: '' })} className="text-xs text-danger">ลบรูป</button>}
            </div>
          </div>

          {/* การอัพรูปใบงาน — เปิด = บังคับถ่ายสด+timestamp / ปิด = อัพจาก Gallery ได้ */}
          <div className="border-t border-line pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="accent-primary h-4 w-4 mt-0.5" checked={form.force_camera}
                onChange={(e) => setForm({ ...form, force_camera: e.target.checked })} />
              <span className="text-sm text-ink">
                บังคับถ่ายรูปสด (ใบงานล้าง)
                <span className="block text-xs text-ink-muted">
                  เปิด = ช่างต้องถ่ายสดหน้างาน + ติด timestamp (เลือกรูปเก่าจาก Gallery ไม่ได้) · ปิด = อัพจาก Gallery ได้
                </span>
              </span>
            </label>
          </div>

          {/* ช่องเซ็นที่ 5 — เปิดเฉพาะสาขาที่ รพ. ขอ (พญาไท นวมินทร์) */}
          <div className="border-t border-line pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" className="accent-primary h-4 w-4 mt-0.5" checked={form.require_department_sign}
                onChange={(e) => setForm({ ...form, require_department_sign: e.target.checked })} />
              <span className="text-sm text-ink">
                ต้องมีลายเซ็น “เจ้าหน้าที่เจ้าของพื้นที่”
                <span className="block text-xs text-ink-muted">
                  เปิด = ใบงานล้างมีช่องเซ็นที่ 5 และต้องเซ็นครบ 5 ช่องจึงวางบิลได้ · ปิด = 4 ช่องตามเดิม
                </span>
              </span>
            </label>
          </div>

          {/* QR */}
          <div className="border-t border-line pt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-ink flex items-center gap-1"><QrCode className="h-4 w-4" /> QR เข้าตรง</span>
              <button onClick={makeQr} className="text-sm text-primary">สร้าง QR</button>
            </div>
            <p className="text-xs text-ink-muted mt-1 break-all">{branchUrl(branch)}</p>
            {qr && (
              <div className="mt-2 flex flex-col items-center gap-2">
                <img src={qr} alt="QR" className="w-44 h-44" />
                <a href={qr} download={`qr-${branch.slug}.png`} className="text-sm text-primary flex items-center gap-1"><Download className="h-4 w-4" /> ดาวน์โหลด QR</a>
              </div>
            )}
          </div>

          {err && <p className="text-sm text-danger bg-danger-soft rounded-input px-3 py-2">{err}</p>}
          <div className="flex gap-2 mt-2">
            <button onClick={onClose} className="btn-secondary flex-1">ปิด</button>
            <button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? 'กำลังบันทึก...' : 'บันทึก'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Branches() {
  const navigate = useNavigate()
  const tenant = useTenantStore()
  const [list, setList] = useState([])
  const [form, setForm] = useState({ code: '', name: '', slug: '', admin_username: '', admin_password: '' })
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(null)

  const load = () => api.get('/branches').then((r) => setList(r.data || [])).catch(() => {})
  useEffect(() => { load() }, [])

  const create = async (e) => {
    e.preventDefault()
    setMsg(null); setBusy(true)
    try {
      const body = { ...form, slug: form.slug || slugify(form.name) }
      const { data } = await api.post('/branches', body)
      setMsg({ ok: true, text: `สร้างสาขา ${data.branch.name} [${data.branch.schema_name}] แล้ว` })
      setForm({ code: '', name: '', slug: '', admin_username: '', admin_password: '' })
      load()
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'สร้างไม่สำเร็จ' })
    } finally { setBusy(false) }
  }

  const toggle = async (b) => { await api.patch(`/branches/${b.id}`, { active: !b.active }).catch(() => {}); load() }
  const manage = (b) => { tenant.switchBranch(b.slug, b.name); navigate('/') }
  const exitBranch = () => { tenant.switchBranch(null) }

  // Provision an existing registry client that has no slug/schema yet → give it a
  // slug + create its schema (reuses POST /branches upsert-by-code + provision).
  const provision = async (b) => {
    const slug = window.prompt(`ตั้ง slug (subdomain) ของ ${b.name}\nเช่น salawakan`, slugify(b.name))
    if (!slug) return
    try {
      await api.post('/branches', { code: b.code, name: b.name, slug: slugify(slug) })
      setMsg({ ok: true, text: `ทำให้ ${b.name} เป็นสาขาแล้ว [${slugify(slug)}]` })
      load()
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'ทำไม่สำเร็จ' })
    }
  }

  return (
    <Layout>
      <div className="p-4 flex flex-col gap-4 max-w-3xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-ink flex items-center gap-2"><Building2 className="h-5 w-5" /> จัดการสาขา</h2>
          {tenant.isBranch && (
            <button onClick={exitBranch} className="text-sm text-ink-muted">← ออกจากสาขา ({tenant.name})</button>
          )}
        </div>

        {/* create */}
        <form onSubmit={create} className="card flex flex-col gap-3">
          <p className="font-semibold text-ink">เพิ่มสาขาใหม่</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">รหัส (code)</label><input className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required /></div>
            <div><label className="label">slug (subdomain)</label><input className="input" placeholder="เว้นว่าง = สร้างจากชื่อ" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
          </div>
          <div><label className="label">ชื่อสาขา</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">admin user (ของสาขา)</label><input className="input" value={form.admin_username} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} /></div>
            <div><label className="label">admin password</label><input className="input" type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /></div>
          </div>
          {msg && <p className={`text-sm rounded-input px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-700' : 'bg-danger-soft text-danger'}`}>{msg.text}</p>}
          <button disabled={busy} className="btn-primary self-start flex items-center gap-2"><Plus className="h-4 w-4" /> {busy ? 'กำลังสร้าง...' : 'สร้างสาขา + schema'}</button>
        </form>

        {/* list */}
        <div className="card">
          <p className="font-semibold text-ink mb-2">สาขาทั้งหมด ({list.length})</p>
          <div className="flex flex-col divide-y divide-line">
            {list.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  {b.card_image
                    ? <img src={b.card_image} alt="" className="w-10 h-10 rounded-lg object-cover border border-line shrink-0" />
                    : <div className="w-10 h-10 rounded-lg bg-primary-soft flex items-center justify-center shrink-0"><Building2 className="h-5 w-5 text-primary" /></div>}
                  <div>
                    <div className="font-medium text-ink">{b.name} <span className="text-xs text-ink-muted">({b.code})</span></div>
                    <div className="text-xs text-ink-muted">{b.subdomain || b.slug ? `${b.subdomain || b.slug}.<domain>` : '— ไม่มี subdomain'} · schema {b.schema_name || '—'} {b.active ? '' : '· ปิดใช้งาน'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {b.slug && b.schema_name ? (
                    <button onClick={() => manage(b)} className="text-sm text-primary flex items-center gap-1"><LogIn className="h-4 w-4" /> เข้าจัดการ</button>
                  ) : (
                    <button onClick={() => provision(b)} className="text-sm text-amber-600 font-medium">⚙ ทำให้เป็นสาขา</button>
                  )}
                  <button onClick={() => setEditing(b)} className="text-xs text-ink-muted hover:text-primary flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> แก้ไข</button>
                  <button onClick={() => toggle(b)} className="text-xs text-ink-muted">{b.active ? 'ปิด' : 'เปิด'}</button>
                </div>
              </div>
            ))}
            {!list.length && <p className="text-sm text-ink-muted py-3">ยังไม่มีสาขา</p>}
          </div>
        </div>
      </div>

      {editing && <EditModal branch={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />}
    </Layout>
  )
}
